/**
 * The one sentence a colleague sees where the composer would be, on a thread
 * they can read but not write (Step 1 / D0).
 *
 * In the THREAD's language, not the reader's UI chrome language, because it
 * sits at the foot of a conversation written in the thread's language and an
 * English line under a Portuguese digest reads as a bug. Same rule as
 * `THREAD_PLACEHOLDERS` in `lib/constants/languages.ts`, and the same
 * fallback: an unpicked or unknown language gets English.
 *
 * Deliberately its own small table rather than a tenth field on
 * `ThreadPlaceholderTemplates`: that type is required-field-per-locale and is
 * consumed by the activity indicators, so a sentence only this page renders
 * does not belong in it.
 *
 * No call to action. There is nothing for this reader to click: the thread is
 * somebody else's, and offering them a door would be offering them a 404.
 */
const READ_ONLY_NOTICE: Record<string, string> = {
  en: 'Only the owner can ask in this thread.',
  es: 'Solo el propietario puede preguntar en este hilo.',
  fr: 'Seul le propriétaire peut poser des questions dans ce fil.',
  de: 'Nur der Eigentümer kann in diesem Thread fragen.',
  pt: 'Somente o proprietário pode perguntar nesta conversa.',
  it: 'Solo il proprietario può fare domande in questa conversazione.',
  nl: 'Alleen de eigenaar kan in dit gesprek vragen stellen.',
  tr: 'Bu konuda yalnızca sahibi soru sorabilir.',
  ja: 'このスレッドで質問できるのはオーナーのみです。',
}

/**
 * `language` is `knowledge_threads.language` as the thread view reports it:
 * null when nobody picked one. A regional tag (`pt-BR`) resolves on its base
 * language, so a thread does not fall back to English over a suffix.
 */
export function readOnlyThreadNotice(
  language: string | null | undefined,
): string {
  const normalized = (language ?? '').trim().toLowerCase()
  const base = normalized.split('-')[0]
  return READ_ONLY_NOTICE[normalized] ?? READ_ONLY_NOTICE[base] ?? READ_ONLY_NOTICE.en
}
